import React, { useState } from 'react';
import { Loader2, Search, UserPlus, X } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import { buscarPessoa } from '../../../src/services/indicacoes';
import { soDigitos, validarPessoaNova, type PessoaForm } from '../../../src/lib/indicacoes/formulario';
import { normalizarWhatsapp } from '../../../src/lib/indicacoes/telefone';
import { SELO_CLIENTE, formatarDataHora } from '../../../src/lib/indicacoes/rotulos';
import { formatarTelefoneBr } from '../../../src/lib/enderecoUc';
import { formatarDocumento } from '../../../src/lib/termoNegociacao';
import type { IndicacaoPessoaBusca } from '../../../types';
import Selo from './Selo';
import { CLASSE_BOTAO_PRIMARIO, CLASSE_BOTAO_SECUNDARIO, CLASSE_INPUT, CLASSE_ROTULO } from './estilos';

/** id nulo = pessoa nova ou cliente nunca abordado (o banco acha pelo CPF ou cria). */
export interface PessoaEscolhida {
  id: string | null;
  nome: string;
  cpf_cnpj: string | null;
  whatsapp: string | null;
  cliente: boolean;
}

interface Props {
  valor: PessoaEscolhida | null;
  onChange: (p: PessoaEscolhida | null) => void;
  gere: boolean;
  rotulo?: string;
}

const PessoaPicker: React.FC<Props> = ({ valor, onChange, gere, rotulo = 'Quem indica' }) => {
  const { addToast } = useToast();
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState<IndicacaoPessoaBusca[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [nova, setNova] = useState(false);
  const [form, setForm] = useState<PessoaForm>({ nome: '', whatsapp: '', cpf_cnpj: '' });

  const buscar = async () => {
    if (!termo.trim()) return;
    setBuscando(true);
    try {
      setResultados(await buscarPessoa(termo.trim()));
    } catch (e) {
      addToast((e as Error).message, 'warning');
      setResultados(null);
    } finally {
      setBuscando(false);
    }
  };

  const usarNova = () => {
    const v = validarPessoaNova(form);
    if (!v.ok) {
      addToast(v.erro, 'warning');
      return;
    }
    onChange({
      id: null,
      nome: form.nome.trim(),
      cpf_cnpj: soDigitos(form.cpf_cnpj) || null,
      whatsapp: normalizarWhatsapp(form.whatsapp),
      cliente: false,
    });
  };

  if (valor) {
    const selo = valor.cliente ? SELO_CLIENTE.sim : SELO_CLIENTE.nao;
    return (
      <div>
        <span className={CLASSE_ROTULO}>{rotulo}</span>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2">
          <div className="min-w-0">
            <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{valor.nome}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 flex flex-wrap gap-x-3">
              {valor.whatsapp && <span>{formatarTelefoneBr(valor.whatsapp)}</span>}
              {valor.cpf_cnpj && <span>{formatarDocumento(valor.cpf_cnpj)}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Selo classe={selo.classe}>{selo.rotulo}</Selo>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-slate-200"
              title="Trocar pessoa"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <span className={CLASSE_ROTULO}>{rotulo}</span>
      {!nova ? (
        <>
          <div className="flex gap-2">
            <input
              className={CLASSE_INPUT}
              value={termo}
              onChange={e => setTermo(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  buscar();
                }
              }}
              placeholder={gere ? 'Nome, CPF/CNPJ, WhatsApp ou número da UC' : 'CPF ou CNPJ completo'}
            />
            <button type="button" onClick={buscar} disabled={buscando || !termo.trim()} className={CLASSE_BOTAO_SECUNDARIO}>
              {buscando ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Buscar
            </button>
          </div>
          {resultados && resultados.length === 0 && (
            <p className="text-xs text-gray-500 dark:text-slate-400">Ninguém encontrado. Cadastre como pessoa nova.</p>
          )}
          {resultados && resultados.length > 0 && (
            <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
              {resultados.map(r => {
                const selo = r.cliente ? SELO_CLIENTE.sim : SELO_CLIENTE.nao;
                return (
                  <li key={r.pessoa_id ?? r.cpf_cnpj ?? r.nome}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center justify-between gap-2"
                      onClick={() =>
                        onChange({ id: r.pessoa_id, nome: r.nome, cpf_cnpj: r.cpf_cnpj, whatsapp: r.whatsapp, cliente: r.cliente })
                      }
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{r.nome}</span>
                        <span className="block text-xs text-gray-500 dark:text-slate-400">
                          {r.abordagens > 0
                            ? `Abordada ${r.abordagens}x · última ${formatarDataHora(r.ultima_abordagem_em)}`
                            : 'Nunca abordada'}
                        </span>
                      </span>
                      <Selo classe={selo.classe}>{selo.rotulo}</Selo>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setNova(true)}
            className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
          >
            <UserPlus size={13} /> Cadastrar pessoa nova
          </button>
        </>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-2">
          <input
            className={CLASSE_INPUT}
            placeholder="Nome"
            value={form.nome}
            onChange={e => setForm({ ...form, nome: e.target.value })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              className={CLASSE_INPUT}
              placeholder="WhatsApp"
              value={form.whatsapp}
              onChange={e => setForm({ ...form, whatsapp: e.target.value })}
            />
            <input
              className={CLASSE_INPUT}
              placeholder="CPF/CNPJ (opcional)"
              value={form.cpf_cnpj}
              onChange={e => setForm({ ...form, cpf_cnpj: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setNova(false)} className={CLASSE_BOTAO_SECUNDARIO}>
              Voltar à busca
            </button>
            <button type="button" onClick={usarNova} className={CLASSE_BOTAO_PRIMARIO}>
              Usar esta pessoa
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PessoaPicker;
